import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CurrencyBtc } from "@phosphor-icons/react";
import api from "@/lib/api";
import { fmtAmount } from "@/lib/util";

// Inline "add tx hash" for approved crypto payments — pastes a hash, calls
// mark-paid (which runs the on-chain check + auto-verifies when tallied), and
// surfaces the outcome as a toast so the payer can act on multiple rows fast
// from the Queue without opening each one.
export default function InlineTxHashDialog({ payment, onClose, onDone }) {
  const [hash, setHash] = useState("");
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const open = !!payment;
  const p = payment;

  const submit = async () => {
    if (!hash.trim()) { toast.error("Paste a tx hash"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/${p.id}/mark-paid`, {
        tx_hash: hash.trim(),
        payment_reference: ref.trim() || null,
      });
      // Backend `mark-paid` fires the on-chain verify inline (crypto only) and
      // sets blockchain.tallied / blockchain.addr_match in the response.
      // Surface each failure mode distinctly so the payer knows exactly why
      // auto-verify didn't fire.
      const bc = data?.blockchain;
      const tallied = bc?.tallied === true;
      const addrOk = bc?.addr_match === true;
      const addrBad = bc?.addr_match === false;
      if (tallied && addrOk) {
        toast.success(`✓ On-chain match · ${fmtAmount(bc.onchain_amount, bc.token || p.currency_code)} → auto-verified`);
      } else if (addrBad) {
        toast.error(`⚠ WRONG WALLET · on-chain recipient does not match ${p.wallet_address ? p.wallet_address.slice(0,12) + "…" : "the request"}. Marked paid but NOT auto-verified.`);
      } else if (bc?.tallied === false) {
        toast.warning(`⚠ On-chain amount mismatch (${fmtAmount(bc.onchain_amount, bc.token || p.currency_code)} vs ${fmtAmount(p.net_amount, p.currency_code)}). Marked paid but NOT auto-verified.`);
      } else if (bc?.verified === false) {
        toast.warning(`Marked paid — on-chain check couldn't confirm yet. Retry from the request detail.`);
      } else {
        toast.success("Marked paid");
      }
      setHash(""); setRef("");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="inline-tx-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CurrencyBtc size={18} weight="bold" className="text-emerald-700" /> Add tx hash & verify
          </DialogTitle>
          <DialogDescription className="text-xs">
            {p && (<>
              <span className="font-mono text-amber-900">{p.reference}</span> · {p.brand_name} · <span className="font-semibold">{fmtAmount(p.net_amount, p.currency_code)}</span> to {p.vendor_name}<br />
              Network: <span className="font-mono">{p.network}</span>
            </>)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Transaction hash *</label>
            <Input
              data-testid="inline-tx-input"
              value={hash}
              onChange={(e) => setHash(e.target.value)}
              placeholder="0x… or Tron / Solana hash"
              className="mt-1 font-mono text-xs"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Reference (optional)</label>
            <Input
              data-testid="inline-tx-ref"
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder="Internal payment reference"
              className="mt-1"
            />
          </div>
          <p className="text-[11px] text-slate-500">
            We'll fetch the transaction from chain, check the amount matches, and auto-verify if it does. If the amounts don't match, the payment is marked paid but flagged for manual review.
          </p>
        </div>

        <DialogFooter>
          <Button data-testid="inline-tx-cancel" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button data-testid="inline-tx-submit" onClick={submit} disabled={busy || !hash.trim()} className="amber-pill rounded-xl">
            {busy ? "Checking…" : "Mark paid & verify"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
